// Time and date utility functions for the calendar

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const HOURS = Array.from({ length: 24 }, (_, i) => i) // All 24 hours (0-23)
export const MINUTES = [0, 15, 30, 45] // 15-minute intervals

// Helper function to check if hour is first in its group
export const isFirstInTimeGroup = (hour: number): boolean => {
  return hour === 0 || hour === 6 || hour === 12 || hour === 18
}

// Get week dates based on offset
export const getWeekDates = (offset: number = 0): Date[] => {
  const today = new Date()
  const currentDay = today.getDay()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - currentDay + offset * 7)

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek)
    date.setDate(startOfWeek.getDate() + i)
    return date
  })
}

// Calculate current time line position with time zone support
export const getCurrentTimePosition = (
  currentTime: Date,
  selectedTimeZone: string,
  weekDates: Date[],
) => {
  const now = currentTime
  const timeZoneDate = new Date(now.toLocaleString('en-US', { timeZone: selectedTimeZone }))
  const currentHour = timeZoneDate.getHours()
  const currentMinutes = timeZoneDate.getMinutes()

  // Calculate position considering hour groupings
  let pixelPosition = 0

  // Add pixels for each complete hour
  for (let h = 0; h < currentHour; h++) {
    // Add group header height if this is the first hour in a group
    if (isFirstInTimeGroup(h)) {
      pixelPosition += 53 // Approximate height of group header
    }
    pixelPosition += 80 // Height of each hour slot (h-20)
  }

  // Add partial height for current minutes
  pixelPosition += (currentMinutes / 60) * 80

  return {
    pixelPosition,
    time: timeZoneDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
    isToday: weekDates.some((date) => date.toDateString() === timeZoneDate.toDateString()),
  }
}

// Calculate scroll position for auto-scroll to current time
export const calculateScrollPosition = (
  currentTime: Date,
  selectedTimeZone: string,
  targetOffsetHours: number = 4,
): number => {
  const now = currentTime
  const timeZoneDate = new Date(now.toLocaleString('en-US', { timeZone: selectedTimeZone }))
  const currentHour = timeZoneDate.getHours()

  // Calculate scroll position accounting for groupings and showing hours before
  let scrollPosition = 0
  const targetHour = Math.max(0, currentHour - targetOffsetHours)

  for (let h = 0; h < targetHour; h++) {
    if (isFirstInTimeGroup(h)) {
      scrollPosition += 53 // Group header height
    }
    scrollPosition += 80 // Hour slot height
  }

  return scrollPosition
}

// Format time for drag preview
export const formatDragTime = (hour: number, minute: number, date: string) => {
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  const displayMinute = minute.toString().padStart(2, '0')
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const dateObj = new Date(date)
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' })
  const monthDay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return {
    time: `${displayHour}:${displayMinute} ${ampm}`,
    date: `${dayName}, ${monthDay}`,
  }
}

// Snap minute to nearest 15-minute interval
export const snapToFifteenMinutes = (minute: number): number => {
  const snappedMinute = Math.round(minute / 15) * 15
  return [0, 15, 30, 45].includes(snappedMinute) ? snappedMinute : 0
}
